/**
 * hog-cut-sheet.js — the storefront builder.
 *
 * Renders everything inside #ng-hog. The Liquid section supplies only images
 * and wording through a JSON block, so this file and the admin's hog.js are
 * the two places cut definitions live, and they have to agree: the sheet this
 * writes is read back by app/lib/hog.js in Farm Ops.
 *
 * ## The sheet
 *
 *   { mirrored: true,
 *     sides: [ { loin: { choice, follow }, shoulder: {...}, ... } ],
 *     shared: { ground: { choice, follow }, extras: { picked: [...] } } }
 *
 * `sides` has one entry for a half, and for a whole either one (mirrored) or
 * two. The five primals go per side because a hog is split down the backbone
 * and each half can be cut its own way. Ground and extras are per animal:
 * grind style is a preference rather than a quantity, and there is one heart
 * per pig.
 *
 * ## The cart
 *
 * A whole hog is two units of the half variant, carrying `_hog: whole`. That
 * is what lets Shopify's stock on a single variant hold the pool — two
 * variants with their own counts can't see each other and will oversell. The
 * property is also how Farm Ops tells one person's animal from two neighbours
 * who each bought a side.
 */
(function () {
  var root = document.getElementById("ng-hog");
  if (!root) return;

  var cfg = {};
  try { cfg = JSON.parse(root.querySelector("[data-ng-hog-config]").textContent); } catch (e) { cfg = {}; }
  var IMG = cfg.images || {};
  var COPY = cfg.copy || {};
  var BASE = cfg.proxyBase || "/apps/csa";

  /* ------------------------------------------------ cut definitions */
  var GRIND = {
    id: "grind", name: "Grind this section",
    desc: "Skip the cuts — send it all to ground or sausage.",
    follow: [{ id: "grind_type", label: "Grind it into", options: [
      { id: "ground", name: "Ground pork" },
      { id: "sausage", name: "Sausage" },
      { id: "both", name: "Half ground, half sausage" }
    ]}]
  };
  var CURE = { id: "cure", label: "Cure and smoke", options: [
    { id: "regular", name: "Regular cure and smoke" },
    { id: "kosher", name: "Traditional kosher cure and smoke" }
  ]};
  var PORTION = { id: "portion", label: "Whole or halves", options: [
    { id: "whole", name: "Leave it whole" },
    { id: "halves", name: "Cut into halves" }
  ]};
  var sausageForm = function (label) {
    return { id: "sausage_form", label: label, options: [
      { id: "loose", name: "Loose in bulk packs" },
      { id: "links", name: "Links" },
      { id: "both", name: "Half loose, half links" }
    ]};
  };

  var SIDE_SECTIONS = [
    { id: "loin", name: "Loin",
      blurb: "The long muscle down the back. This is where your chops come from.",
      note: "The tenderloin comes with this section no matter what you pick.",
      options: [
        { id: "bone_in_chops", name: "Bone-in chops", desc: "Cut about an inch thick, two to a pack." },
        { id: "boneless_chops", name: "Boneless chops", desc: "Rib bones come off, which frees up the baby backs.",
          follow: [{ id: "babyback", label: "Baby back ribs",
            help: "Taking the bone out of your chops leaves a rack of baby backs. Want them?",
            options: [{ id: "yes", name: "Yes, pack the baby backs" }, { id: "no", name: "No thanks" }] }] },
        { id: "roast", name: "Loin roast", desc: "Left whole as roasts instead of sliced into chops." },
        GRIND
      ]},
    { id: "shoulder", name: "Shoulder",
      blurb: "Hard-working muscle with good fat running through it. Forgiving, slow-cooking meat.",
      options: [
        { id: "roasts", name: "Shoulder roasts", desc: "Butt and picnic roasts. What you want for pulled pork." },
        { id: "steaks", name: "Shoulder steaks", desc: "Sliced thick for the grill or the skillet." },
        GRIND
      ]},
    { id: "belly", name: "Belly",
      blurb: "The bacon slab. Leave it fresh or send it to the smokehouse.",
      options: [
        { id: "fresh", name: "Fresh pork belly", desc: "Uncured slab. Roast it, braise it, or cure it yourself." },
        { id: "bacon", name: "Bacon", desc: "Cured, smoked, and sliced.", follow: [CURE] },
        GRIND
      ]},
    { id: "ham", name: "Ham",
      blurb: "The back leg. The biggest single piece of meat on the hog.",
      options: [
        // Bacon is the only cut with a cure choice. Hams are regular cure and
        // smoke, so the question isn't asked here.
        { id: "smoked_roast", name: "Smoked ham roast", desc: "Cured and smoked, then packed as roasts.", follow: [PORTION] },
        { id: "fresh_roast", name: "Fresh ham roast", desc: "No cure, no smoke. Roasts like a big pork roast.", follow: [PORTION] },
        { id: "steaks", name: "Ham steaks", desc: "Sliced across the leg.",
          follow: [{ id: "steak_style", label: "Fresh or smoked", options: [
            { id: "fresh", name: "Fresh ham steaks" },
            { id: "smoked", name: "Smoked ham steaks" }
          ]}]},
        GRIND
      ]},
    { id: "ribs", name: "Ribs",
      blurb: "Spare ribs off the side of the hog, meatier and fattier than baby backs.",
      options: [
        { id: "whole", name: "Whole rack", desc: "Full slab, untrimmed." },
        { id: "halved", name: "Halved racks", desc: "Cut in two — easier to fit a smoker or a freezer shelf." },
        GRIND
      ]}
  ];

  var SHARED_SECTIONS = [
    { id: "ground", name: "Ground and sausage", shared: true,
      blurb: "Trim from every section lands here, plus anything you sent to grind along the way.",
      note: "Asked once for the whole animal — the grinder runs one batch, so it can't be split by side.",
      options: [
        { id: "all_ground", name: "All ground pork", desc: "Plain, one-pound packs." },
        { id: "all_sausage", name: "All sausage", desc: "Seasoned, loose or cased.", follow: [sausageForm("How do you want the sausage")] },
        { id: "split", name: "Split it", desc: "Half ground pork, half sausage.", follow: [sausageForm("How do you want the sausage half")] }
      ]},
    { id: "extras", name: "Extras", shared: true, multi: true, optional: true,
      blurb: "Everything that usually gets thrown out. Take it or leave it.",
      note: "There's one heart and one liver per pig, so this one covers the whole animal too.",
      options: [
        { id: "fat", name: "Fat", desc: "Leaf fat and fatback for rendering lard." },
        { id: "organs", name: "Organs", desc: "Heart, liver, and kidneys." },
        { id: "bones", name: "Soup bones", desc: "Neck and knuckle bones for stock." }
      ]}
  ];

  var SECTIONS = SIDE_SECTIONS.concat(SHARED_SECTIONS);

  var MODES = [
    { id: "now", t: "Buy my hog and pick my cuts now",
      d: "Walk through every section, then check out with your cut sheet attached." },
    { id: "later", t: "Buy my hog and pick my cuts later",
      d: "Reserve the hog today. We'll send you this same cut sheet before we haul to the butcher." },
    { id: "existing", t: "I already bought my hog — here are my cuts",
      d: "Pull up your order and send us your cut sheet." }
  ];

  /* ------------------------------------------------ state */
  var state = {
    mode: null,
    batches: null,      // null = not fetched, [] = none open
    batchId: null,
    size: null,         // whole | half
    mirrored: true,
    side: 0,            // which side is being edited
    current: 0,         // which section
    sheets: [{}, {}],   // per side, primals only
    shared: {},
    claim: { order: "", email: "" },
    lookup: null,       // { loading, message, reservations }
    signedIn: null,     // null = haven't asked the proxy yet
    reservation: null,  // the one being edited in `existing` mode
    saved: false,
    busy: false
  };

  var $ = function (s) { return root.querySelector(s); };
  var esc = function (s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  };
  var find = function (id) {
    for (var i = 0; i < SECTIONS.length; i++) if (SECTIONS[i].id === id) return SECTIONS[i];
  };
  var opt = function (sec, id) {
    for (var i = 0; i < sec.options.length; i++) if (sec.options[i].id === id) return sec.options[i];
  };
  var batch = function () {
    if (!state.batches) return null;
    for (var i = 0; i < state.batches.length; i++) {
      if (state.batches[i].id === state.batchId) return state.batches[i];
    }
    return null;
  };

  /** Sides actually in play: one for a half, one for a mirrored whole, else two. */
  var sideCount = function () {
    return state.size === "whole" && !state.mirrored ? 2 : 1;
  };

  function placeholder(label) {
    return "data:image/svg+xml;utf8," + encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 120"><rect width="160" height="120" fill="#E4E7DE"/>' +
      '<path d="M0 96 C24 74 36 70 52 80 C70 91 78 66 100 58 C124 49 140 76 160 86 L160 120 L0 120Z" fill="#D3D8CA"/>' +
      '<text x="80" y="112" font-family="sans-serif" font-size="9" fill="#79806E" text-anchor="middle">' +
      String(label).replace(/&/g, "&amp;") + "</text></svg>");
  }
  function thumb(sec, o) {
    return IMG[sec.id + ":" + o.id] || (o.id === "grind" ? IMG.grind : "") || placeholder(o.name);
  }

  /* ------------------------------------------------ completeness */
  function sheetFor(sec) {
    return sec.shared ? state.shared : (state.sheets[state.side] || {});
  }
  function sectionDone(sec, sheet) {
    var data = sheet || {};
    if (sec.multi) return (data[sec.id] && (data[sec.id].picked || []).length) > 0;
    var entry = data[sec.id];
    if (!entry || !entry.choice) return false;
    var o = opt(sec, entry.choice);
    if (!o) return false;
    return (o.follow || []).every(function (g) {
      return entry.follow && entry.follow[g.id];
    });
  }
  function sideDone(i) {
    return SIDE_SECTIONS.every(function (s) { return sectionDone(s, state.sheets[i]); });
  }
  function requiredCount() {
    return SIDE_SECTIONS.length * sideCount() + 1; // + ground; extras optional
  }
  function doneCount() {
    var n = 0;
    for (var i = 0; i < sideCount(); i++) {
      SIDE_SECTIONS.forEach(function (s) { if (sectionDone(s, state.sheets[i])) n++; });
    }
    if (sectionDone(find("ground"), state.shared)) n++;
    return n;
  }
  function ready() {
    if (!state.size) return false;
    if (state.mode !== "existing" && !state.batchId) return false;
    return doneCount() === requiredCount();
  }

  /* ------------------------------------------------ shell */
  var HOG_PATH = "M36,116 C44,98 60,85 82,77 C85,58 97,48 109,53 C119,57 122,69 123,80 " +
    "C141,66 162,59 190,57 C250,51 332,51 392,61 C418,65 434,79 438,97 C443,88 450,85 452,90 " +
    "C455,97 447,103 441,107 C440,120 438,124 434,134 C430,152 428,176 432,198 C434,206 438,210 434,214 " +
    "C428,217 418,217 412,214 C407,205 405,196 403,178 C401,160 395,150 387,146 C360,162 320,172 272,174 " +
    "C232,176 206,172 196,166 C192,180 188,196 190,208 C191,212 193,213 190,214 C184,217 172,217 166,214 " +
    "C163,205 160,196 158,180 C156,164 153,152 149,146 C130,158 100,158 78,150 C60,144 46,132 36,116 Z";

  var REGIONS = [
    { id: "shoulder", x: 128, y: 40, w: 102, h: 200, lx: 179, ly: 120 },
    { id: "loin", x: 230, y: 40, w: 122, h: 78, lx: 291, ly: 86 },
    { id: "ribs", x: 230, y: 118, w: 66, h: 122, lx: 263, ly: 152 },
    { id: "belly", x: 296, y: 118, w: 56, h: 122, lx: 324, ly: 152 },
    { id: "ham", x: 352, y: 40, w: 120, h: 200, lx: 405, ly: 120 }
  ];

  function shell() {
    return '<div class="ng-wrap"><h1>' + esc(COPY.heading || "Build your hog") + "</h1>" +
      '<p class="ng-lede" id="ngLede"></p><div id="ngBody"></div></div>' +
      '<div class="ng-bar" id="ngBar" hidden>' +
        '<div class="ng-bar-top" id="ngBarTop"><div class="ng-prog"><span id="ngBarFill"></span></div>' +
        '<span class="ng-prog-txt" id="ngBarTxt"></span><span class="ng-caret">&#9650;</span></div>' +
        '<div class="ng-bar-sheet" id="ngBarSheet"></div>' +
        '<button class="ng-btn ng-btn--go" id="ngBarGo">Review and finish</button></div>' +
      '<dialog id="ngOut"><div class="ng-modal"><h2 style="font-size:22px">Your cut sheet</h2>' +
        '<p class="ng-sub">Preview only — on the live store this goes to the cart.</p>' +
        '<pre id="ngOutBody"></pre><div class="ng-nav"><button class="ng-btn" id="ngOutClose">Keep editing</button></div>' +
      "</div></dialog>";
  }

  function mapHTML() {
    var active = SECTIONS[state.current].id;
    var sheet = state.sheets[state.side];
    var rects = REGIONS.map(function (r) {
      var sec = find(r.id), cls = "ng-region";
      if (sectionDone(sec, sheet)) cls += " is-done";
      if (active === r.id) cls += " is-active";
      return '<rect class="' + cls + '" data-section="' + r.id + '" x="' + r.x + '" y="' + r.y +
        '" width="' + r.w + '" height="' + r.h + '"><title>' + esc(sec.name) + "</title></rect>";
    }).join("");
    var labels = REGIONS.map(function (r) {
      return '<text class="ng-region-label' + (active === r.id ? " is-active" : "") + '" x="' + r.lx +
        '" y="' + r.ly + '" text-anchor="middle">' + esc(find(r.id).name) + "</text>";
    }).join("");
    var chips = SHARED_SECTIONS.map(function (s) {
      return '<button type="button" class="ng-chip' + (sectionDone(s, state.shared) ? " is-done" : "") +
        (active === s.id ? " is-active" : "") + '" data-section="' + s.id + '">' +
        (sectionDone(s, state.shared) ? "\u2713 " : "") + esc(s.name) + "</button>";
    }).join("");

    return '<div class="ng-map"><svg viewBox="0 0 560 250" role="group" aria-label="Sections of the hog">' +
      '<defs><clipPath id="ngHogClip"><path d="' + HOG_PATH + '"/></clipPath></defs>' +
      '<g clip-path="url(#ngHogClip)"><rect x="0" y="40" width="128" height="200" fill="#F2F3EE"/>' + rects + "</g>" +
      '<path class="ng-hog-line" d="' + HOG_PATH + '"/>' +
      '<circle cx="80" cy="103" r="3.6" fill="currentColor"/>' +
      '<ellipse cx="45" cy="112" rx="3.4" ry="4.6" fill="currentColor" opacity=".55"/>' +
      labels + "</svg>" + '<div class="ng-chips">' + chips + "</div></div>";
  }

  /** Only shown when a whole hog is being cut two ways. */
  function sideSwitchHTML() {
    if (sideCount() < 2) return "";
    if (SECTIONS[state.current].shared) {
      return '<p class="ng-sub" style="margin-top:14px">This one covers the whole animal, both sides.</p>';
    }
    var buttons = [0, 1].map(function (i) {
      return '<button type="button" class="ng-side" data-side="' + i + '" aria-pressed="' + (state.side === i) + '">' +
        (sideDone(i) ? '<span class="tick">\u2713</span>' : "") + "Side " + (i + 1) + "</button>";
    }).join("");
    return '<div class="ng-sides"><span class="lbl">Cutting</span>' + buttons +
      '<button type="button" class="ng-back" data-mirror="1" style="margin:0 0 0 6px">' +
      "Cut both sides the same instead</button></div>";
  }

  function pickHTML(label, items) {
    return '<p class="ng-step">' + esc(label) + '</p><div class="ng-picks">' + items + "</div>";
  }

  function batchPickHTML() {
    if (state.batches === null) {
      return '<p class="ng-step">Loading what\u2019s available\u2026</p>';
    }
    if (!state.batches.length) {
      return '<div class="ng-panel"><h2>Nothing for sale right now</h2>' +
        '<p class="ng-blurb">We\u2019re between batches. Get in touch and we\u2019ll tell you when the next pigs go in.</p></div>';
    }
    var items = state.batches.map(function (b) {
      var left = b.remaining;
      var cls = left <= 0 ? "is-out" : left <= 3 ? "is-low" : "";
      var label = pigsLeft(left);
      return '<button type="button" data-batch="' + b.id + '"' + (left <= 0 ? " disabled" : "") +
        ' aria-pressed="' + (state.batchId === b.id) + '">' +
        '<span class="t">' + esc(b.name) + "</span>" +
        '<span class="d">Butchered ' + esc(prettyDate(b.processingDate)) + "</span>" +
        '<span class="ng-left ' + cls + '">' + label + "</span></button>";
    }).join("");
    return pickHTML("Which batch?", items);
  }

  function sizePickHTML() {
    var b = batch();
    if (!b) return "";
    var items = [
      { id: "whole", name: COPY.whole_name || "Whole hog",
        desc: COPY.whole_desc || "The entire animal, cut your way.",
        off: !b.canSellWhole, offLabel: "Only a half left" },
      { id: "half", name: COPY.half_name || "Half hog",
        desc: COPY.half_desc || "One side of the hog, same choices, half the packages.", off: false }
    ].map(function (s) {
      return '<button type="button" data-size="' + s.id + '"' + (s.off ? " disabled" : "") +
        ' aria-pressed="' + (state.size === s.id) + '">' +
        '<span class="t">' + esc(s.name) + '</span><span class="d">' + esc(s.desc) + "</span>" +
        (s.off ? '<span class="ng-left is-out">' + esc(s.offLabel) + "</span>" : "") + "</button>";
    }).join("");
    return pickHTML("Whole or half?", items);
  }

  /** Asked once, after a whole hog is chosen. Most people want both alike. */
  function mirrorPickHTML() {
    if (state.size !== "whole") return "";
    var items = [
      { v: "1", t: "Cut both sides the same", d: "One set of choices, applied to the whole hog." },
      { v: "0", t: "Cut each side differently", d: "Chops one way on one side, another on the other." }
    ].map(function (m) {
      return '<button type="button" data-mirror="' + m.v + '" aria-pressed="' +
        (state.mirrored === (m.v === "1")) + '">' +
        '<span class="t">' + m.t + '</span><span class="d">' + m.d + "</span></button>";
    }).join("");
    return pickHTML("How should we cut it?", items);
  }

  function claimHTML() {
    var l = state.lookup || {};
    return '<div class="ng-claim"><h3 style="font-size:18px">Find your order</h3>' +
      '<p class="ng-sub">Your order number and the email you bought with.</p>' +
      '<div class="ng-fields">' +
      '<div><label for="ngOrder">Order number</label><input id="ngOrder" data-claim="order" value="' +
        esc(state.claim.order) + '" placeholder="#1234"></div>' +
      '<div><label for="ngEmail">Email</label><input id="ngEmail" type="email" data-claim="email" value="' +
        esc(state.claim.email) + '" autocomplete="email"></div>' +
      "</div>" +
      '<div style="margin-top:12px"><button type="button" class="ng-btn" data-lookup="1"' +
        (l.loading ? " disabled" : "") + ">" + (l.loading ? "Looking\u2026" : "Find my order") + "</button></div>" +
      (l.message ? '<p class="ng-msg is-bad">' + esc(l.message) + "</p>" : "") +
      "</div>";
  }

  function reservationPickHTML() {
    var l = state.lookup;
    if (!l || !l.reservations || !l.reservations.length) return "";
    if (l.reservations.length === 1 && state.reservation) return "";
    var items = l.reservations.map(function (r) {
      return '<button type="button" data-res="' + r.id + '" aria-pressed="' +
        (state.reservation && state.reservation.id === r.id) + '">' +
        '<span class="t">' + (r.size === "whole" ? "Whole hog" : "Half hog") + "</span>" +
        '<span class="d">' + esc(r.batchName) + " \u00b7 order " + esc(r.orderNumber) + "</span>" +
        '<span class="ng-left ' + (r.status === "submitted" ? "" : "is-low") + '">' +
        (r.status === "submitted" ? "Cuts already in" : r.status === "late" ? "Past the deadline" :
          r.status === "locked" ? "With the butcher" : "No cuts yet") + "</span></button>";
    }).join("");
    return pickHTML("Which one?", items);
  }

  function followHTML(sec, entry, g) {
    var picked = entry.follow ? entry.follow[g.id] : null;
    var note = "";
    var pills = g.options.map(function (o) {
      if (o.id === picked && o.note) note = o.note;
      return '<button type="button" class="ng-pill" data-follow="' + g.id + '" data-val="' + o.id +
        '" aria-pressed="' + (picked === o.id) + '">' + esc(o.name) + "</button>";
    }).join("");
    return '<div class="ng-follow"><h3>' + esc(g.label) + "</h3>" +
      (g.help ? '<p class="ng-sub">' + esc(g.help) + "</p>" : "") +
      '<div class="ng-pills">' + pills + "</div>" +
      (note ? '<p class="ng-sub">' + esc(note) + "</p>" : "") + "</div>";
  }

  function panelHTML() {
    var sec = SECTIONS[state.current];
    var sheet = sheetFor(sec);
    var entry = sheet[sec.id] || {};
    var body;

    if (sec.multi) {
      var picked = entry.picked || [];
      body = '<div class="ng-checks">' + sec.options.map(function (o) {
        return '<div class="ng-check" role="button" tabindex="0" data-extra="' + o.id + '" aria-pressed="' +
          (picked.indexOf(o.id) > -1) + '"><img src="' + thumb(sec, o) + '" alt="">' +
          '<div><span class="ng-name">' + esc(o.name) + '</span><span class="ng-desc">' +
          esc(o.desc) + "</span></div></div>";
      }).join("") + "</div>";
    } else {
      body = '<div class="ng-opts">' + sec.options.map(function (o) {
        return '<button type="button" class="ng-opt" data-choice="' + o.id + '" aria-pressed="' +
          (entry.choice === o.id) + '"><img class="ng-thumb" src="' + thumb(sec, o) + '" alt="">' +
          '<span class="ng-name">' + esc(o.name) + '</span><span class="ng-desc">' +
          esc(o.desc || "") + "</span></button>";
      }).join("") + "</div>";
      if (entry.choice) {
        var o = opt(sec, entry.choice);
        body += (o.follow || []).map(function (g) { return followHTML(sec, entry, g); }).join("");
      }
    }

    var last = state.current === SECTIONS.length - 1;
    var heading = sec.name;
    if (!sec.shared && sideCount() > 1) heading += " \u00b7 side " + (state.side + 1);

    return '<div class="ng-panel"><p class="ng-step">Section ' + (state.current + 1) +
      " of " + SECTIONS.length + "</p><h2>" + esc(heading) + "</h2>" +
      '<p class="ng-blurb">' + esc(sec.blurb) + "</p>" +
      (sec.note ? '<p class="ng-note">' + esc(sec.note) + "</p>" : "") +
      sideSwitchHTML() + body +
      '<div class="ng-nav"><button class="ng-btn" data-step="-1"' +
        (state.current === 0 && state.side === 0 ? " disabled" : "") + ">Back</button>" +
      '<button class="ng-btn ng-btn--go" data-step="1">' +
        (last ? "Review your cut sheet" : "Next section") + "</button></div></div>";
  }

  /* ------------------------------------------------ summary */
  function describe(sections, sheet) {
    return sections.map(function (sec) {
      var entry = (sheet || {})[sec.id] || {};
      if (sec.multi) {
        var names = (entry.picked || []).map(function (id) {
          var o = opt(sec, id); return o ? o.name : null;
        }).filter(Boolean);
        return { id: sec.id, k: sec.name, v: names.length ? names.join(", ") : "Nothing added",
          empty: !names.length };
      }
      if (!entry.choice) return { id: sec.id, k: sec.name, v: "Not set yet", empty: true };
      var o = opt(sec, entry.choice);
      var bits = (o.follow || []).map(function (g) {
        var v = entry.follow ? entry.follow[g.id] : null;
        for (var i = 0; i < g.options.length; i++) if (g.options[i].id === v) return g.options[i].name;
        return "\u2026";
      });
      return { id: sec.id, k: sec.name, v: [o.name].concat(bits).join(" \u00b7 ") };
    });
  }

  function linesHTML(list, side) {
    return '<ul class="ng-lines">' + list.map(function (l) {
      return '<li data-jump="' + l.id + '"' + (side == null ? "" : ' data-jumpside="' + side + '"') +
        '><span class="ng-k">' + esc(l.k) + '</span><span class="ng-v' +
        (l.empty ? " is-empty" : "") + '">' + esc(l.v) + "</span></li>";
    }).join("") + "</ul>";
  }

  function summaryHTML() {
    var n = doneCount(), total = requiredCount();
    var b = batch();
    var head = state.mode === "existing" && state.reservation
      ? state.reservation.batchName + " \u00b7 " + (state.size === "whole" ? "whole hog" : "half hog")
      : (b ? b.name + (state.size ? " \u00b7 " + (state.size === "whole" ? "whole hog" : "half hog") : "")
        : "Pick a batch to start");

    var body = "";
    if (sideCount() > 1) {
      body += '<p class="ng-group">Side 1</p>' + linesHTML(describe(SIDE_SECTIONS, state.sheets[0]), 0);
      body += '<p class="ng-group">Side 2</p>' + linesHTML(describe(SIDE_SECTIONS, state.sheets[1]), 1);
      body += '<p class="ng-group">Whole animal</p>' + linesHTML(describe(SHARED_SECTIONS, state.shared));
    } else {
      body += linesHTML(describe(SIDE_SECTIONS, state.sheets[0]), 0);
      body += linesHTML(describe(SHARED_SECTIONS, state.shared));
    }

    var cta = state.mode === "existing" ? "Send my cut sheet" : "Add to cart and check out";
    return "<h3>Your cut sheet</h3>" +
      '<p class="ng-prog-txt">' + esc(head) + "</p>" +
      '<div class="ng-prog"><span style="width:' + (total ? (n / total) * 100 : 0) + '%"></span></div>' +
      '<p class="ng-prog-txt">' + n + " of " + total + " sections set</p>" + body +
      '<button class="ng-btn ng-btn--go" data-go="1"' + (ready() && !state.busy ? "" : " disabled") + ">" +
      (state.busy ? "Working\u2026" : cta) + "</button>";
  }

  /* ------------------------------------------------ render */
  /**
   * Halves to pigs, in words a customer thinks in.
   *
   * The pool is counted in halves because that keeps the arithmetic whole, but
   * nobody buying a hog thinks "eleven halves". An odd count is the interesting
   * case — it means one animal has a side still going, which reads as "5½ pigs
   * left" rather than as a rounding error.
   */
  function pigsLeft(halves) {
    if (halves <= 0) return "Sold out";
    if (halves === 1) return "Half a pig left";
    var whole = Math.floor(halves / 2);
    var odd = halves % 2;
    return whole + (odd ? "\u00bd" : "") + " pig" + (whole === 1 && !odd ? "" : "s") + " left";
  }

  function prettyDate(iso) {
    if (!iso) return "";
    var d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  }

  function render() {
    var lede = $("#ngLede"), body = $("#ngBody"), bar = $("#ngBar");

    if (!state.mode) {
      lede.textContent = COPY.intro || "Start here — how would you like to do this?";
      body.innerHTML = '<div class="ng-gate">' + MODES.map(function (m) {
        return '<button type="button" data-mode="' + m.id + '"><span class="t">' + esc(m.t) +
          '</span><span class="d">' + esc(m.d) + "</span></button>";
      }).join("") + "</div>";
      bar.hidden = true;
      return;
    }

    if (state.saved) {
      lede.textContent = "";
      body.innerHTML = '<button class="ng-back" data-mode="">Start over</button>' +
        '<div class="ng-done-card"><h2>Got it</h2>' +
        '<p class="ng-blurb" style="margin:8px auto 0">Your cut sheet is on your order. ' +
        "We'll be in touch before butcher day if anything needs sorting out.</p></div>";
      bar.hidden = true;
      return;
    }

    var back = '<button class="ng-back" data-mode="">Start over</button>';

    if (state.mode === "later") {
      lede.textContent = "Reserve your hog now. We'll email you this cut sheet in plenty of time.";
      body.innerHTML = back + batchPickHTML() +
        (state.batchId ? sizePickHTML() : "") +
        (state.size
          ? '<div class="ng-panel"><h2>That\u2019s all we need for now</h2>' +
            '<p class="ng-blurb">Check out and your hog is spoken for. When it\u2019s time we\u2019ll send ' +
            'a link back to this page, and you\u2019ll pick your cuts then.</p>' +
            '<div class="ng-nav"><span></span><button class="ng-btn ng-btn--go" data-go="1"' +
            (state.busy ? " disabled" : "") + ">" +
            (state.busy ? "Working\u2026" : "Reserve my hog") + "</button></div></div>"
          : "");
      bar.hidden = true;
      return;
    }

    // The two modes that fill in a sheet.
    var setup, ready_;
    if (state.mode === "existing") {
      var known = state.signedIn && state.lookup && (state.lookup.reservations || []).length;
      lede.textContent = known
        ? "Here's what you bought. Tell us how you'd like it cut."
        : "Pull up your order, then tell us how you'd like it cut.";
      setup = (known ? "" : claimHTML()) + reservationPickHTML();
      ready_ = Boolean(state.reservation);
    } else {
      lede.textContent = "Pick a batch, then walk the carcass one section at a time. "
        + "Nothing is final until you check out.";
      setup = batchPickHTML() + (state.batchId ? sizePickHTML() : "") +
        (state.size === "whole" ? mirrorPickHTML() : "");
      ready_ = Boolean(state.batchId && state.size);
    }

    if (!ready_) {
      body.innerHTML = back + setup;
      bar.hidden = true;
      return;
    }

    body.innerHTML = back + setup +
      '<div class="ng-cols"><div>' + mapHTML() + panelHTML() + "</div>" +
      '<aside class="ng-summary">' + summaryHTML() + "</aside></div>";

    bar.hidden = false;
    $("#ngBarSheet").innerHTML = summaryHTML();
    var n = doneCount(), total = requiredCount();
    $("#ngBarFill").style.width = (total ? (n / total) * 100 : 0) + "%";
    $("#ngBarTxt").textContent = n + "/" + total;
    var go = $("#ngBarGo");
    go.disabled = !ready() || state.busy;
    go.textContent = state.mode === "existing" ? "Send my cut sheet" : "Add to cart and check out";
  }

  function goTo(id, side) {
    if (side != null) state.side = side;
    for (var i = 0; i < SECTIONS.length; i++) if (SECTIONS[i].id === id) state.current = i;
    render();
    var p = root.querySelector(".ng-panel");
    if (p && window.innerWidth <= 900) p.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /**
   * Next and Back walk sides as well as sections.
   *
   * With two sides that's loin-to-ribs for side one, the same again for side
   * two, then the shared sections once. Without it, "next" from the last
   * primal would skip a whole side of the animal.
   */
  function step(dir) {
    var sec = SECTIONS[state.current];
    var lastPrimal = SIDE_SECTIONS.length - 1;

    if (dir > 0) {
      if (!sec.shared && state.current === lastPrimal && sideCount() > 1 && state.side === 0) {
        state.side = 1; state.current = 0; return;
      }
      if (state.current === SECTIONS.length - 1) return submit();
      state.current++;
      return;
    }

    if (sec.shared && state.current === SIDE_SECTIONS.length && sideCount() > 1) {
      state.side = 1; state.current = lastPrimal; return;
    }
    if (state.current === 0 && sideCount() > 1 && state.side === 1) {
      state.side = 0; state.current = lastPrimal; return;
    }
    state.current = Math.max(0, state.current - 1);
  }

  /* ------------------------------------------------ events */
  root.addEventListener("click", function (e) {
    var t = e.target, el;

    if ((el = t.closest("[data-mode]"))) {
      var mode = el.getAttribute("data-mode") || null;
      state.mode = mode;
      state.current = 0; state.side = 0; state.saved = false;
      if (!mode) {
        state.batchId = null; state.size = null; state.reservation = null;
        state.lookup = null; state.sheets = [{}, {}]; state.shared = {};
      }
      render();
      if (mode && mode !== "existing" && state.batches === null) loadBatches();
      if (mode === "existing" && state.signedIn === null) loadMine();
      return;
    }

    if ((el = t.closest("[data-batch]"))) {
      state.batchId = el.getAttribute("data-batch");
      var b = batch();
      if (state.size === "whole" && b && !b.canSellWhole) state.size = null;
      return render();
    }

    if ((el = t.closest("[data-size]"))) {
      state.size = el.getAttribute("data-size");
      if (state.size === "half") { state.mirrored = true; state.side = 0; }
      return render();
    }

    if ((el = t.closest("[data-mirror]"))) {
      var wasSplit = !state.mirrored;
      state.mirrored = el.getAttribute("data-mirror") === "1";
      // Going split, side two starts as a copy of side one so nobody begins
      // again from nothing. Going back to mirrored, side one wins.
      if (!state.mirrored && !wasSplit) {
        state.sheets[1] = JSON.parse(JSON.stringify(state.sheets[0]));
      }
      if (state.mirrored) state.side = 0;
      return render();
    }

    if ((el = t.closest("[data-side]"))) {
      state.side = Number(el.getAttribute("data-side"));
      return render();
    }

    if ((el = t.closest("[data-res]"))) {
      var id = el.getAttribute("data-res");
      var list = (state.lookup && state.lookup.reservations) || [];
      for (var i = 0; i < list.length; i++) if (list[i].id === id) adoptReservation(list[i]);
      return render();
    }

    if (t.closest("[data-lookup]")) return lookup();

    if ((el = t.closest("[data-section]"))) return goTo(el.getAttribute("data-section"));
    if ((el = t.closest("[data-jump]"))) {
      var s = el.getAttribute("data-jumpside");
      return goTo(el.getAttribute("data-jump"), s == null ? null : Number(s));
    }

    if ((el = t.closest("[data-choice]"))) {
      var sec = SECTIONS[state.current];
      var sheet = sheetFor(sec);
      var v = el.getAttribute("data-choice");
      var cur = sheet[sec.id] || {};
      sheet[sec.id] = cur.choice === v ? {} : { choice: v, follow: {} };
      return render();
    }

    if ((el = t.closest("[data-follow]"))) {
      var sec2 = SECTIONS[state.current];
      var sheet2 = sheetFor(sec2);
      var entry = sheet2[sec2.id] || (sheet2[sec2.id] = { follow: {} });
      entry.follow = entry.follow || {};
      entry.follow[el.getAttribute("data-follow")] = el.getAttribute("data-val");
      return render();
    }

    if ((el = t.closest("[data-extra]"))) {
      var sec3 = SECTIONS[state.current];
      var sheet3 = sheetFor(sec3);
      var entry3 = sheet3[sec3.id] || (sheet3[sec3.id] = { picked: [] });
      entry3.picked = entry3.picked || [];
      var id3 = el.getAttribute("data-extra");
      var at = entry3.picked.indexOf(id3);
      if (at > -1) entry3.picked.splice(at, 1); else entry3.picked.push(id3);
      return render();
    }

    if ((el = t.closest("[data-step]"))) {
      step(Number(el.getAttribute("data-step")));
      render();
      var p = root.querySelector(".ng-panel");
      if (p) p.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (t.closest("[data-go]") || t.id === "ngBarGo") return submit();
    if (t.id === "ngOutClose") return $("#ngOut").close();
    if (t.closest("#ngBarTop")) $("#ngBar").classList.toggle("is-open");
  });

  root.addEventListener("input", function (e) {
    var el = e.target.closest("[data-claim]");
    if (!el) return;
    state.claim[el.getAttribute("data-claim")] = el.value;
  });

  root.addEventListener("keydown", function (e) {
    var el = e.target.closest("[data-extra]");
    if (el && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); el.click(); }
    if (e.key === "Enter" && e.target.closest("[data-claim]")) { e.preventDefault(); lookup(); }
  });

  /* ------------------------------------------------ data */
  function loadBatches() {
    fetch(BASE + "/hog-batches", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (d) { state.batches = d.batches || []; render(); })
      .catch(function () { state.batches = []; render(); });
  }

  /**
   * Ask who's signed in before showing anyone a lookup form.
   *
   * The app proxy hands Farm Ops a logged_in_customer_id for a signed-in
   * storefront visitor, so their hogs come back without an order number being
   * typed. Someone signed out gets the form, which is the common case — most
   * people buying a hog have never made an account.
   */
  function loadMine(then) {
    fetch(BASE + "/hog-cuts?mine=1")
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var list = (d.signedIn && d.reservations) || [];
        state.signedIn = Boolean(d.signedIn);
        if (list.length) {
          state.lookup = { reservations: list };
          if (list.length === 1) adoptReservation(list[0]);
        }
        render();
        if (then) then(list.length);
      })
      .catch(function () { render(); });
  }

  function lookup() {
    if (!state.claim.order || !state.claim.email) {
      state.lookup = { message: "We need both the order number and the email." };
      return render();
    }
    state.lookup = { loading: true };
    render();
    fetch(BASE + "/hog-cuts?order=" + encodeURIComponent(state.claim.order) +
      "&email=" + encodeURIComponent(state.claim.email))
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var list = d.reservations || [];
        state.lookup = { reservations: list, message: d.message || d.error || null };
        if (list.length === 1) adoptReservation(list[0]);
        render();
      })
      .catch(function () {
        state.lookup = { message: "Couldn't reach us just then. Try again in a moment." };
        render();
      });
  }

  /** Load an existing reservation into the builder, cuts and all. */
  function adoptReservation(r) {
    state.reservation = r;
    state.size = r.size;
    state.mirrored = r.sheet.mirrored !== false;
    state.side = 0;
    state.current = 0;
    var sides = r.sheet.sides || [];
    state.sheets = [sides[0] || {}, sides[1] || {}];
    state.shared = r.sheet.shared || {};
  }

  /* ------------------------------------------------ submit */
  function sheetOut() {
    var sides = [];
    for (var i = 0; i < sideCount(); i++) sides.push(state.sheets[i] || {});
    return { mirrored: state.mirrored, sides: sides, shared: state.shared };
  }

  /** Readable properties, so the order itself says what was ordered. */
  function readableProperties() {
    var out = {};
    if (sideCount() > 1) {
      describe(SIDE_SECTIONS, state.sheets[0]).forEach(function (l) { out["Side 1 " + l.k] = l.v; });
      describe(SIDE_SECTIONS, state.sheets[1]).forEach(function (l) { out["Side 2 " + l.k] = l.v; });
    } else {
      describe(SIDE_SECTIONS, state.sheets[0]).forEach(function (l) { out[l.k] = l.v; });
    }
    describe(SHARED_SECTIONS, state.shared).forEach(function (l) { out[l.k] = l.v; });
    return out;
  }

  function numericId(gid) {
    return String(gid || "").split("/").pop();
  }

  function submit() {
    if (state.busy) return;
    if (state.mode === "later") {
      if (!state.batchId || !state.size) return;
      return addToCart(null);
    }
    if (!ready()) return nudge();
    if (state.mode === "existing") return sendSheet();
    addToCart(sheetOut());
  }

  /** Send them to whatever is unfinished rather than just refusing. */
  function nudge() {
    if (state.mode !== "existing" && !state.batchId) {
      return root.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (!state.size) return root.scrollIntoView({ behavior: "smooth", block: "start" });
    for (var s = 0; s < sideCount(); s++) {
      for (var i = 0; i < SIDE_SECTIONS.length; i++) {
        if (!sectionDone(SIDE_SECTIONS[i], state.sheets[s])) return goTo(SIDE_SECTIONS[i].id, s);
      }
    }
    if (!sectionDone(find("ground"), state.shared)) return goTo("ground");
  }

  function addToCart(sheet) {
    var b = batch();
    if (!b) return;

    var properties = {};
    if (sheet) {
      properties = readableProperties();
      properties._cut_sheet = JSON.stringify(sheet);
    } else {
      properties["Cut sheet"] = "Customer will submit cuts later";
    }

    var item;
    if (state.size === "whole" && b.wholeAsTwoHalves) {
      // Two of the half variant, marked as one animal. Shopify's stock on that
      // variant is what actually stops the batch overselling.
      properties._hog = "whole";
      item = { id: numericId(b.halfVariantId), quantity: 2, properties: properties };
    } else if (state.size === "whole") {
      item = { id: numericId(b.wholeVariantId), quantity: 1, properties: properties };
    } else {
      item = { id: numericId(b.halfVariantId), quantity: 1, properties: properties };
    }

    if (!window.Shopify) {
      return preview(JSON.stringify({ items: [item] }, null, 2));
    }

    state.busy = true; render();
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || "Sold out"); });
        window.location.href = "/checkout";
      })
      .catch(function (err) {
        state.busy = false;
        // Almost always the batch selling out between loading the page and
        // checking out, so refresh the counts rather than just apologising.
        state.lookup = null;
        loadBatches();
        render();
        alert(err.message + "\n\nAvailability has been refreshed — have another look.");
      });
  }

  function sendSheet() {
    if (!state.reservation) return;
    state.busy = true; render();
    fetch(BASE + "/hog-cuts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reservationId: state.reservation.id,
        // Ignored by the endpoint when it can identify the customer itself.
        order: state.claim.order,
        email: state.claim.email,
        sheet: sheetOut()
      })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        state.busy = false;
        if (!res.ok) {
          state.lookup = { reservations: (state.lookup || {}).reservations || [], message: res.d.error };
          return render();
        }
        state.saved = true;
        render();
      })
      .catch(function () {
        state.busy = false;
        state.lookup = { reservations: (state.lookup || {}).reservations || [],
          message: "Couldn't reach us just then. Try again in a moment." };
        render();
      });
  }

  function preview(text) {
    $("#ngOutBody").textContent = text;
    $("#ngOut").showModal();
  }

  root.innerHTML = shell();

  // The customer portal links here with #my-hogs. Open on that path rather
  // than making someone who just clicked "change my cuts" choose again.
  if (/(^|#)my-hogs?$/.test(window.location.hash)) {
    state.mode = "existing";
    loadMine();
  }

  render();
})();